import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api/client';
import type {
  Product,
  SyncRun,
  SyncSource,
  SyncSourceKind,
  SyncStats,
  SyncTestResult,
} from '../api/types';
import { IconKey, IconPlus, IconRefresh, IconSync } from '../components/icons';
import { Pill } from '../components/license';
import {
  Button,
  Callout,
  CheckLine,
  ConfirmDialog,
  Dialog,
  EmptyState,
  ErrorNote,
  SecretReveal,
  SelectField,
  SkeletonRows,
  TextAreaField,
  TextField,
  useToast,
} from '../components/ui';
import { useCapabilities } from '../lib/auth';
import { formatDateTime, formatRelative } from '../lib/license';

/**
 * External systems Heron mirrors companies, expiry and logins from.
 *
 * Each source is another product's database. Heron reads it (SELECT only) on
 * its own interval and keeps companies, licenses and logins in step. That
 * system stays where those records are created and edited.
 */

const KIND_LABEL: Record<SyncSourceKind, string> = {
  presoft_portal: 'Presoft License Portal',
  custom_sql: 'Custom SQL',
};

const EXAMPLE_COMPANIES_SQL = `SELECT cust_no      AS id,
       cust_name    AS name,
       active       AS is_active,   -- or: status
       valid_until  AS expires_at   -- NULL = never expires
  FROM customers`;

const EXAMPLE_LOGINS_SQL = `SELECT user_id       AS id,
       email         AS username,
       password_hash AS password_hash,   -- bcrypt or Argon2
       cust_no       AS company_id,      -- matches the companies "id"
       enabled       AS is_active
  FROM users`;

export function SourcesPage() {
  const can = useCapabilities();
  const [editing, setEditing] = useState<SyncSource | 'new' | null>(null);

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['sync-sources'],
    queryFn: () => api.get<{ sources: SyncSource[] }>('/api/staff/sync-sources'),
    refetchInterval: 30_000,
  });
  const sources = data?.sources ?? [];

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Sources</h1>
          <p className="page-head__sub">
            Other systems whose companies, expiry dates and logins Heron copies in automatically.
            Each system stays where those records are created and edited.
          </p>
        </div>
        {can.canWrite && (
          <div className="page-head__actions">
            <Button variant="primary" icon={<IconPlus />} onClick={() => setEditing('new')}>
              Add source
            </Button>
          </div>
        )}
      </div>

      {isPending && (
        <section className="panel">
          <SkeletonRows rows={3} />
        </section>
      )}
      {isError && (
        <section className="panel">
          <div className="panel__body">
            <ErrorNote error={error} />
          </div>
        </section>
      )}

      {!isPending && !isError && sources.length === 0 && (
        <section className="panel">
          <EmptyState
            icon={<IconSync size={30} />}
            title="No sources yet"
            body="Add a system's database and Heron will copy its companies, expiry dates and logins in, then keep them up to date."
            action={
              can.canWrite ? (
                <Button variant="primary" icon={<IconPlus />} onClick={() => setEditing('new')}>
                  Add source
                </Button>
              ) : undefined
            }
          />
        </section>
      )}

      {sources.map((source) => (
        <SourceCard key={source.id} source={source} canWrite={can.canWrite} canDestroy={can.canDestroy} onEdit={() => setEditing(source)} />
      ))}

      {editing && (
        <SourceDialog
          source={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------

function SourceCard({
  source,
  canWrite,
  canDestroy,
  onEdit,
}: {
  source: SyncSource;
  canWrite: boolean;
  canDestroy: boolean;
  onEdit: () => void;
}) {
  const queryClient = useQueryClient();
  const { push } = useToast();
  const [showHistory, setShowHistory] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [nudge, setNudge] = useState<{ token: string; path: string } | null>(null);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['sync-sources'] });
    void queryClient.invalidateQueries({ queryKey: ['sync-overview'] });
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    void queryClient.invalidateQueries({ queryKey: ['companies'] });
  };

  const run = useMutation({
    mutationFn: () =>
      api.post<{ ok: boolean; error?: string; skippedBecause?: string }>(
        `/api/staff/sync-sources/${source.id}/run`,
      ),
    onSuccess: (outcome) => {
      refresh();
      push(
        outcome.ok
          ? { tone: 'success', title: `${source.name} synced` }
          : { tone: 'error', title: `${source.name} did not sync`, body: outcome.error },
      );
    },
  });

  const toggle = useMutation({
    mutationFn: () =>
      api.patch(`/api/staff/sync-sources/${source.id}`, { isEnabled: !source.is_enabled }),
    onSuccess: refresh,
  });

  const issueToken = useMutation({
    mutationFn: () =>
      api.post<{ token: string; path: string }>(`/api/staff/sync-sources/${source.id}/nudge-token`),
    onSuccess: (data) => {
      refresh();
      setNudge(data);
    },
  });

  const remove = useMutation({
    mutationFn: () => api.del(`/api/staff/sync-sources/${source.id}`),
    onSuccess: () => {
      refresh();
      setDeleting(false);
      push({ tone: 'success', title: `${source.name} removed` });
    },
  });

  const stats = source.last_run_stats ?? {};
  const skipped = stats.skipped ?? [];
  const warnings = stats.warnings ?? [];

  return (
    <section className="panel">
      <div className="panel__head">
        <div>
          <div className="row" style={{ gap: 'var(--s2)', flexWrap: 'wrap' }}>
            <h2 className="panel__title">{source.name}</h2>
            <Pill tone={source.is_enabled ? 'live' : 'neutral'}>
              {source.is_enabled ? 'On' : 'Paused'}
            </Pill>
            <Pill>{KIND_LABEL[source.kind]}</Pill>
          </div>
          <p className="panel__hint">
            Feeds <strong>{source.product_name}</strong> · reads{' '}
            <span className="mono">{source.db_target}</span> every{' '}
            {formatInterval(source.interval_seconds)}
          </p>
        </div>
        <div className="sync-source__actions">
          <Button size="sm" icon={<IconRefresh />} loading={run.isPending} onClick={() => run.mutate()}>
            Sync now
          </Button>
          {canWrite && (
            <Button size="sm" onClick={onEdit}>
              Edit
            </Button>
          )}
        </div>
      </div>

      <div className="panel__body stack">
        <div className="summary summary--compact">
          <Stat label="Companies" value={source.company_count} />
          <Stat label="Logins" value={source.login_count} />
          <Stat
            label="Last sync"
            text={source.last_success_at ? formatRelative(source.last_success_at) : 'never'}
          />
        </div>

        {source.last_run_ok === false && (
          <Callout tone="danger" title={`Last sync failed ${formatRelative(source.last_run_at)}`}>
            {source.last_run_error}
          </Callout>
        )}
        {run.isError && <ErrorNote error={run.error} />}

        {skipped.length > 0 && (
          <Callout tone="warn" title={`${skipped.length} not copied in`}>
            <ul className="sync-list">
              {skipped.slice(0, 8).map((item) => (
                <li key={`${item.kind}:${item.ref}`}>
                  <span className="mono">{item.ref}</span> — {item.reason}
                </li>
              ))}
              {skipped.length > 8 && <li>…and {skipped.length - 8} more (see History).</li>}
            </ul>
          </Callout>
        )}
        {warnings.length > 0 && (
          <Callout tone="info" title="Worth knowing">
            <ul className="sync-list">
              {warnings.slice(0, 6).map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          </Callout>
        )}

        <div className="row" style={{ gap: 'var(--s2)', flexWrap: 'wrap' }}>
          <Button size="sm" variant="ghost" onClick={() => setShowHistory(true)}>
            History
          </Button>
          {canWrite && (
            <>
              <Button size="sm" variant="ghost" loading={toggle.isPending} onClick={() => toggle.mutate()}>
                {source.is_enabled ? 'Pause' : 'Resume'}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                icon={<IconKey />}
                loading={issueToken.isPending}
                onClick={() => issueToken.mutate()}
              >
                {source.has_nudge_token ? 'New instant-sync token' : 'Instant-sync token'}
              </Button>
            </>
          )}
          {/* Removing a source is a destroy — admin only. */}
          {canDestroy && (
            <Button size="sm" variant="ghost" onClick={() => setDeleting(true)}>
              Remove
            </Button>
          )}
        </div>
      </div>

      {showHistory && <HistoryDialog source={source} onClose={() => setShowHistory(false)} />}

      {nudge && (
        <Dialog open onClose={() => setNudge(null)} title={`Instant sync for ${source.name}`} wide>
          <div className="stack">
            <Callout tone="info" title="Optional">
              Without this Heron still syncs every {formatInterval(source.interval_seconds)}. With
              it, the other system can ask Heron to sync straight after it saves something.
            </Callout>
            <SecretReveal label="URL (POST)" value={`${window.location.origin}${nudge.path}`} />
            <SecretReveal label="Header x-heron-sync-token" value={nudge.token} />
            {source.kind === 'presoft_portal' && (
              <p className="small muted">
                For the Presoft License Portal, set these on its Railway service:{' '}
                <span className="mono">HERON_SYNC_URL</span> = the URL above and{' '}
                <span className="mono">HERON_SYNC_TOKEN</span> = the token.
              </p>
            )}
            <Callout tone="warn" title="Shown once">
              Heron keeps only a hash of the token. Any earlier token for this source has stopped
              working.
            </Callout>
            <div className="form-actions">
              <Button variant="primary" onClick={() => setNudge(null)}>
                Done
              </Button>
            </div>
          </div>
        </Dialog>
      )}

      <ConfirmDialog
        open={deleting}
        onClose={() => setDeleting(false)}
        onConfirm={() => remove.mutate()}
        title={`Remove ${source.name}?`}
        body={
          <>
            Heron stops reading this system. The companies, logins and licenses it created stay in
            Heron as ordinary records you can edit by hand — nothing is deleted or switched off.
          </>
        }
        confirmLabel="Remove source"
        confirmWord={source.name}
        loading={remove.isPending}
        error={remove.error}
      />
    </section>
  );
}

function Stat({ label, value, text }: { label: string; value?: number; text?: string }) {
  return (
    <div className="summary__item">
      <span className="summary__value">{text ?? value}</span>
      <span className="summary__label">{label}</span>
    </div>
  );
}

function formatInterval(seconds: number): string {
  if (seconds < 60) return `${seconds} seconds`;
  if (seconds < 3600) return `${Math.round(seconds / 60)} min`;
  return `${Math.round(seconds / 3600)} h`;
}

// ---------------------------------------------------------------------------

function HistoryDialog({ source, onClose }: { source: SyncSource; onClose: () => void }) {
  const { data, isPending, isError, error } = useQuery({
    queryKey: ['sync-runs', source.id],
    queryFn: () => api.get<{ runs: SyncRun[] }>(`/api/staff/sync-sources/${source.id}/runs`),
  });

  return (
    <Dialog open onClose={onClose} title={`${source.name} — recent syncs`} wide>
      {isPending && <SkeletonRows rows={4} />}
      {isError && <ErrorNote error={error} />}
      {data && data.runs.length === 0 && <p className="muted">No syncs yet.</p>}
      {data && data.runs.length > 0 && (
        <div className="table-wrap scroll-area">
          <table className="table">
            <thead>
              <tr>
                <th>When</th>
                <th>Trigger</th>
                <th>Result</th>
                <th>Changes</th>
              </tr>
            </thead>
            <tbody>
              {data.runs.map((run) => (
                <tr key={run.id}>
                  <td className="cell-meta tabular nowrap">{formatDateTime(run.started_at)}</td>
                  <td className="cell-meta">{run.trigger}</td>
                  <td>
                    {run.ok === null ? (
                      <Pill tone="pending">Running</Pill>
                    ) : run.ok ? (
                      <Pill tone="live">OK</Pill>
                    ) : (
                      <span className="cell-stack">
                        <Pill tone="expired">Failed</Pill>
                        <span className="cell-meta">{run.error}</span>
                      </span>
                    )}
                  </td>
                  <td className="cell-meta">{run.ok ? describeChanges(run.stats) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Dialog>
  );
}

function describeChanges(stats: SyncStats): string {
  const parts: string[] = [];
  const c = stats.companies;
  const l = stats.licenses;
  const u = stats.users;
  if (c?.created) parts.push(`${c.created} new compan${c.created === 1 ? 'y' : 'ies'}`);
  if (c?.linked) parts.push(`${c.linked} matched`);
  if (l?.created || l?.updated) parts.push(`${(l?.created ?? 0) + (l?.updated ?? 0)} license change(s)`);
  if (l?.revoked) parts.push(`${l.revoked} revoked`);
  if (u?.created) parts.push(`${u.created} new login(s)`);
  if (u?.passwordsChanged) parts.push(`${u.passwordsChanged} password(s)`);
  if (u?.deactivated) parts.push(`${u.deactivated} switched off`);
  if (stats.skipped?.length) parts.push(`${stats.skipped.length} skipped`);
  return parts.length ? parts.join(' · ') : 'no changes';
}

// ---------------------------------------------------------------------------

function SourceDialog({ source, onClose }: { source: SyncSource | null; onClose: () => void }) {
  const queryClient = useQueryClient();
  const { push } = useToast();
  const isNew = source === null;

  const [name, setName] = useState(source?.name ?? '');
  const [kind, setKind] = useState<SyncSourceKind>(source?.kind ?? 'presoft_portal');
  const [dbUrl, setDbUrl] = useState('');
  const [productId, setProductId] = useState(source?.product_id ?? '');
  const [companiesSql, setCompaniesSql] = useState(source?.companies_sql ?? '');
  const [loginsSql, setLoginsSql] = useState(source?.logins_sql ?? '');
  const [interval, setIntervalSeconds] = useState(String(source?.interval_seconds ?? 60));
  const [enabled, setEnabled] = useState(source?.is_enabled ?? true);
  const [test, setTest] = useState<SyncTestResult | null>(null);

  const products = useQuery({
    queryKey: ['products'],
    queryFn: () => api.get<{ products: Product[] }>('/api/staff/products'),
  });

  const reading = {
    kind,
    companiesSql: kind === 'custom_sql' ? companiesSql : null,
    loginsSql: kind === 'custom_sql' ? loginsSql : null,
  };

  const runTest = useMutation({
    mutationFn: () =>
      api.post<SyncTestResult>('/api/staff/sync-sources/test', {
        ...reading,
        dbUrl: dbUrl.trim() || undefined,
        sourceId: source?.id,
      }),
    onSuccess: setTest,
  });

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name,
        ...reading,
        productId,
        intervalSeconds: Number(interval) || 60,
        isEnabled: enabled,
      };
      return isNew
        ? api.post('/api/staff/sync-sources', { ...body, dbUrl })
        : api.patch(`/api/staff/sync-sources/${source!.id}`, {
            ...body,
            ...(dbUrl.trim() ? { dbUrl } : {}),
          });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['sync-sources'] });
      void queryClient.invalidateQueries({ queryKey: ['sync-overview'] });
      void queryClient.invalidateQueries({ queryKey: ['companies'] });
      push({ tone: 'success', title: isNew ? `${name} added — first sync done` : `${name} saved` });
      onClose();
    },
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={isNew ? 'Add source' : `Edit ${source!.name}`}
      hint="Heron only reads this database (SELECT, inside a read-only transaction). It never writes to it."
      wide
    >
      <form
        className="stack"
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate();
        }}
      >
        {save.isError && <ErrorNote error={save.error} />}

        <div className="form-grid form-grid--2">
          <TextField
            label="Name"
            value={name}
            required
            autoFocus={isNew}
            placeholder="e.g. Pos-Dashboard license"
            onChange={(e) => setName(e.currentTarget.value)}
          />
          <SelectField
            label="Type of system"
            value={kind}
            onChange={(e) => {
              setKind(e.currentTarget.value as SyncSourceKind);
              setTest(null);
            }}
          >
            <option value="presoft_portal">Presoft License Portal (built in)</option>
            <option value="custom_sql">Any other system (custom SQL)</option>
          </SelectField>
        </div>

        <TextField
          label="Database address"
          type="password"
          value={dbUrl}
          required={isNew}
          mono
          autoComplete="off"
          placeholder="postgresql://user:password@host:port/database"
          hint={
            isNew
              ? 'Postgres connection URL. On Railway, use the database’s DATABASE_PUBLIC_URL. Stored encrypted; never shown again.'
              : `Currently ${source!.db_target}. Leave blank to keep it.`
          }
          onChange={(e) => {
            setDbUrl(e.currentTarget.value);
            setTest(null);
          }}
        />

        <div className="form-grid form-grid--2">
          <SelectField
            label="Heron product it feeds"
            value={productId}
            required
            hint="Its companies get a license for this product, with the system's expiry."
            onChange={(e) => setProductId(e.currentTarget.value)}
          >
            <option value="" disabled>
              Choose a product…
            </option>
            {(products.data?.products ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} ({p.code})
              </option>
            ))}
          </SelectField>
          <TextField
            label="Sync every (seconds)"
            type="number"
            min={15}
            max={86400}
            value={interval}
            hint="15 or more. 60 is a good default."
            onChange={(e) => setIntervalSeconds(e.currentTarget.value)}
          />
        </div>

        {kind === 'custom_sql' && (
          <>
            <Callout tone="info" title="Two queries, renamed to Heron's columns">
              Write a SELECT for companies and one for logins, using <span className="mono">AS</span>{' '}
              to rename the system's own columns. Required:{' '}
              <span className="mono">id, name</span> for companies and{' '}
              <span className="mono">id, username, password_hash, company_id</span> for logins.
              Optional: <span className="mono">expires_at</span>, <span className="mono">is_active</span>{' '}
              or <span className="mono">status</span>, <span className="mono">email</span>,{' '}
              <span className="mono">notes</span>. Passwords must be bcrypt or Argon2.
            </Callout>
            <TextAreaField
              label="Companies query"
              value={companiesSql}
              rows={6}
              spellCheck={false}
              style={{ fontFamily: 'var(--font-mono)', fontSize: 13 }}
              placeholder={EXAMPLE_COMPANIES_SQL}
              onChange={(e) => {
                setCompaniesSql(e.currentTarget.value);
                setTest(null);
              }}
            />
            <TextAreaField
              label="Logins query"
              value={loginsSql}
              rows={7}
              spellCheck={false}
              style={{ fontFamily: 'var(--font-mono)', fontSize: 13 }}
              placeholder={EXAMPLE_LOGINS_SQL}
              onChange={(e) => {
                setLoginsSql(e.currentTarget.value);
                setTest(null);
              }}
            />
          </>
        )}

        <CheckLine
          title="Sync automatically"
          description="Off = only when someone presses Sync now."
          checked={enabled}
          onChange={setEnabled}
        />

        {runTest.isError && <ErrorNote error={runTest.error} />}
        {test && <TestResult result={test} />}

        <div className="form-actions">
          <Button
            onClick={() => runTest.mutate()}
            loading={runTest.isPending}
            disabled={isNew && !dbUrl.trim()}
          >
            Test
          </Button>
          <span className="grow" />
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" loading={save.isPending} disabled={!productId}>
            {isNew ? 'Add and sync' : 'Save'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function TestResult({ result }: { result: SyncTestResult }) {
  if (!result.ok) {
    return (
      <Callout tone="danger" title="Heron could not read it">
        {result.error}
      </Callout>
    );
  }
  const formats = Object.entries(result.passwordFormats ?? {})
    .map(([f, n]) => `${n} ${f}`)
    .join(', ');
  const unsupported = result.passwordFormats?.unsupported ?? 0;

  return (
    <div className="stack">
      <Callout tone={unsupported ? 'warn' : 'info'} title="Connected and read">
        Found <strong>{result.companies}</strong> companies and <strong>{result.logins}</strong>{' '}
        logins. Passwords: {formats || 'none'}.
        {unsupported > 0 && ` ${unsupported} use a format Heron cannot check and will be skipped.`}
        {result.loginsWithoutCompany
          ? ` ${result.loginsWithoutCompany} login(s) point at a company id the companies query does not return.`
          : ''}
      </Callout>
      {result.problems && result.problems.length > 0 && (
        <Callout tone="warn" title="Rows that will be ignored">
          <ul className="sync-list">
            {result.problems.slice(0, 6).map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </Callout>
      )}
      {result.sample && result.sample.companies.length > 0 && (
        <div className="table-wrap scroll-area">
          <table className="table">
            <thead>
              <tr>
                <th>Sample company</th>
                <th>Status</th>
                <th>Expires</th>
              </tr>
            </thead>
            <tbody>
              {result.sample.companies.map((c) => (
                <tr key={c.externalId}>
                  <td>{c.name}</td>
                  <td className="cell-meta">{c.status}</td>
                  <td className="cell-meta tabular">{c.expiresAt ? formatDateTime(c.expiresAt) : 'No expiry'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
